import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ChatSession, MoveToProjectModalResult, RenameChatModalResult } from '../../models/chat.models';
import { AIAgentService } from '../../services/ai-agent.service';
import { DeoOwnershipService } from '../../services/deo-ownership.service';
import { getApiErrorMessage } from '../../services/api-error.helper';
import { GainsApplyCancelService } from 'app/shared/services/gains-apply-delete-service/gains-apply-delete.service';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DeoAiAssistantRenameChatComponent } from '../deo-ai-assistant-rename-chat/deo-ai-assistant-rename-chat.component';
import { DeoAiAssistantMoveToProjectComponent } from '../deo-ai-assistant-move-to-project/deo-ai-assistant-move-to-project.component';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';
import { FeatureFlagsService } from 'app/shared/services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_DEO_AGENT_PROJECTS, FEATURE_FLAG_DEO_AGENT_RENAME_CHAT } from 'app/shared/services/feature-flags/feature-flags.constants';
import { isTempSessionId } from '../../models/temp-session.constants';

@Component({
  selector: 'deo-ai-assistant-chats-view',
  standalone: false,
  templateUrl: './deo-ai-assistant-chats-view.component.html',
  styleUrls: ['./deo-ai-assistant-chats-view.component.scss']
})
export class DeoAiAssistantChatsViewComponent implements OnInit {
  @Input() allChats: ChatSession[] = [];
  @Input() currentSessionId: string | null = null;
  @Input() isLoading = false;
  /** Backend failure message from the host's chat-sessions load, or null when it succeeded. */
  @Input() loadError: string | null = null;
  /** Scopes the list to a single project, enabling the "Remove from Project" action. */
  @Input() isProjectView = false;

  @Output() createNewChatRequested = new EventEmitter<void>();
  @Output() chatSelected = new EventEmitter<string>();
  @Output() chatDeleted$ = new EventEmitter<string[]>();
  @Output() chatRemovedFromProject$ = new EventEmitter<string>();
  /** Asks the host to re-run the chat-sessions load after a failure. */
  @Output() retry$ = new EventEmitter<void>();

  selectedSessionIds: string[] = [];
  projectModeIsOpen = false;
  searchControl = new FormControl('');
  modalRef: any;
  selectedChatSession: ChatSession | null = null;
  isProjectsEnabled = false;

  chatMenuItems: EllipsisMenuItem[] = [
    {
      title: 'Rename',
      icon: 'rename.svg',
      id: 'rename'
    },
    {
      title: 'Move to Project',
      icon: 'MoveProject.svg',
      id: 'move-to-project'
    },
    {
      title: 'Remove from Project',
      icon: 'MoveProject.svg',
      id: 'remove-from-project'
    },
    {
      title: 'Delete',
      icon: 'trash-2.svg',
      id: 'delete',
      warning: true,
      divider: true
    }
  ];

  constructor(
    private readonly _gainsToastService: GainsToastService,
    private readonly _gainsApplyCancelService: GainsApplyCancelService,
    private readonly _modalService: NzModalService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _aiAgentService: AIAgentService,
    private readonly _ownershipService: DeoOwnershipService
  ) {}

  /** A chat another user keeps in a shared project can be opened and read, never changed. */
  canManage(session: ChatSession): boolean {
    return this._ownershipService.isMine(session.user_id);
  }

  ngOnInit(): void {
    this.isProjectsEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_PROJECTS);

    if (!this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_RENAME_CHAT)) {
      this.chatMenuItems = this.chatMenuItems.filter(item => item.title !== 'Rename');
    }

    if (!this.isProjectsEnabled) {
      this.chatMenuItems = this.chatMenuItems.filter(item => item.title !== 'Move to Project' && item.title !== 'Remove from Project');
    }

    // "Remove from Project" only makes sense when the list is scoped to a project.
    if (!this.isProjectView) {
      this.chatMenuItems = this.chatMenuItems.filter(item => item.title !== 'Remove from Project');
    }

    if (this.chatMenuItems[0]?.divider) {
      this.chatMenuItems = [{ ...this.chatMenuItems[0], divider: false }, ...this.chatMenuItems.slice(1)];
    }
  }

  onChatMenuAction(action: string, chat: ChatSession): void {
    if (action === 'Rename') {
      this.renameChat(chat.id, null);
    }

    if (action === 'Move to Project') {
      this.moveChatToProject(chat, null);
    }

    if (action === 'Remove from Project') {
      this.removeChatFromProject(chat);
    }

    if (action === 'Delete') {
      this.deleteChatConfirmation(chat, null);
    }
  }

  private removeChatFromProject(chat: ChatSession): void {
    if (isTempSessionId(chat.id)) {
      this.chatRemovedFromProject$.emit(chat.id);
      return;
    }

    this._aiAgentService.setSessionProject(chat.id, null).subscribe({
      next: response => {
        if (!response.succeeded) {
          this._gainsToastService.error('Error', 'Failed to remove chat from project. Please try again.');
          return;
        }
        this.chatRemovedFromProject$.emit(chat.id);
        this._gainsToastService.success('Chat Removed', `The chat <strong>"${chat.title}"</strong> has been removed from the project.`);
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to remove chat from project. Please try again.'));
      }
    });
  }

  get filteredChats(): ChatSession[] {
    const q = this.searchControl.value?.toLowerCase().trim();
    const filtered = q ? this.allChats.filter(c => c.title.toLowerCase().includes(q)) : this.allChats;
    return this.sortStarredFirst(filtered);
  }

  /** Array.sort is stable, so the order the backend sent survives inside each group. */
  private sortStarredFirst(chats: ChatSession[]): ChatSession[] {
    return [...chats].sort((a, b) => Number(!!b.isFavorite) - Number(!!a.isFavorite));
  }

  createNewChat(): void {
    this.createNewChatRequested.emit();
  }

  /** The sessions payload carries the artifact total (PLAN-2188), so the session itself is the source. */
  getArtifactCount(session: ChatSession): number {
    return session.artifactCount ?? 0;
  }

  trackBySessionId(_: number, session: ChatSession): string {
    return session.id;
  }

  selectChat(sessionId: string): void {
    this.chatSelected.emit(sessionId);
  }

  isSessionSelected(sessionId: string): boolean {
    return this.selectedSessionIds.includes(sessionId);
  }

  /** Space would otherwise toggle the input natively, which fights the [checked] binding. */
  onCheckboxKey(event: Event, sessionId: string): void {
    event.preventDefault();
    this.toggleSessionSelection(sessionId, event);
  }

  toggleSessionSelection(sessionId: string, event?: any): void {
    event?.stopPropagation();
    const index = this.selectedSessionIds.indexOf(sessionId);
    if (index > -1) {
      this.selectedSessionIds.splice(index, 1);
    } else {
      this.selectedSessionIds.push(sessionId);
    }
  }

  toggleStar(sessionId: string, event: any): void {
    event.stopPropagation();
    const session = this.allChats.find(s => s.id === sessionId);
    if (session) {
      session.isFavorite = !session.isFavorite;
      this._aiAgentService.updateFavorite(sessionId, !!session.isFavorite);
      const title = session.isFavorite ? 'Chat Starred' : 'Chat Unstarred';
      const message = session.isFavorite ? `The chat <strong>"${session.title}"</strong> has been starred.` : `The chat <strong>"${session.title}"</strong> has been unstarred.`;
      this._gainsToastService.success(title, message);
    }
  }

  formatDate(date: string | Date): string {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (messageDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString();
    }
  }

  renameChat(sessionId: string, event: any): void {
    event?.stopPropagation();
    const session = this.allChats.find(s => s.id === sessionId);
    if (session) {
      this.openRenameChatModal(sessionId, session.title);
    }
  }
  openRenameChatModal(sessionId: string, originalChatName: string): void {
    this.modalRef = this._modalService.create({
      nzContent: DeoAiAssistantRenameChatComponent,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzWidth: 540,
      nzOnCancel: () => this.modalRef.close(),
      nzData: { sessionId, originalChatName },
      nzBodyStyle: {
        padding: '0'
      }
    });

    this.modalRef.afterClose.subscribe((result: RenameChatModalResult | undefined) => {
      if (!result?.newChatName) {
        return;
      }

      this.renameChatSession(result.sessionId, result.newChatName, originalChatName);
    });
  }

  private renameChatSession(sessionId: string, newChatName: string, originalChatName: string): void {
    this._aiAgentService.renameSession(sessionId, newChatName).subscribe({
      next: response => {
        if (!response.succeeded) {
          this._gainsToastService.error('Error', 'Failed to rename chat. Please try again.');
          return;
        }
        this.renameChatInUI(sessionId, newChatName);
        this._gainsToastService.success('Chat Renamed', `The chat <strong>"${originalChatName}"</strong> has been renamed to <strong>"${newChatName}"</strong>.`);
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to rename chat. Please try again.'));
      }
    });
  }
  renameChatInUI(sessionId: any, newChatName: any) {
    const session = this.allChats.find(s => s.id === sessionId);
    if (session) {
      session.title = newChatName;
    }
  }

  bulkDeleteSessions(event: any): void {
    if (this.selectedSessionIds.length === 0) return;
    if (this.selectedSessionIds.length === 1) {
      const sessionId = Array.from(this.selectedSessionIds)[0];
      const session = this.allChats.find(s => s.id === sessionId);
      this.deleteChatConfirmation(session, event);
    } else {
      this.deleteChatsConfirmation(event);
    }
  }
  deleteChatsConfirmation(event: any): void {
    event.stopPropagation();

    this._gainsApplyCancelService.delete(
      'Delete Chats',
      `<div><div>Are you sure you want to delete the selected chats? This action cannot be undone.</div><div class="padding-top-10">Are you sure you want to proceed?</div></div>`,
      () => {
        this.onHandleDeletedChats();
      },
      () => {
        // Cancel action
      }
    );
  }

  deleteChatConfirmation(session: any, event: any): void {
    event?.stopPropagation();

    this._gainsApplyCancelService.delete(
      'Delete Chat',
      `<div><div>Are you sure you want to delete chat "${session.title}"? This action cannot be undone.</div><div class="padding-top-10">Are you sure you want to proceed?</div></div>`,
      () => {
        this.onHandleDeletedChat(session);
      },
      () => {
        // Cancel action
      }
    );
  }
  onHandleDeletedChat(session: ChatSession) {
    if (isTempSessionId(session.id)) {
      this.chatDeleted$.emit([session.id]);
      this._gainsToastService.success('Chat Deleted', `<strong>${session.title}</strong> has been successfully deleted.`);
      return;
    }

    this._aiAgentService.deleteChatSession(session.id).subscribe({
      next: response => {
        if (!response.succeeded) {
          this._gainsToastService.error('Error', 'Failed to delete chat. Please try again.');
          return;
        }
        this.chatDeleted$.emit([session.id]);
        this._gainsToastService.success('Chat Deleted', `<strong>${session.title}</strong> has been successfully deleted.`);
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to delete chat. Please try again.'));
      }
    });
  }
  onHandleDeletedChats() {
    const sessionIds = [...this.selectedSessionIds];
    if (sessionIds.length === 0) {
      return;
    }

    const realSessionIds = sessionIds.filter(sessionId => !isTempSessionId(sessionId));
    if (realSessionIds.length === 0) {
      this.finalizeBulkDelete(sessionIds);
      return;
    }

    forkJoin(realSessionIds.map(sessionId => this._aiAgentService.deleteChatSession(sessionId))).subscribe({
      next: responses => {
        if (!responses.every(response => response.succeeded)) {
          this._gainsToastService.error('Error', 'Failed to delete one or more chats. Please try again.');
          return;
        }
        this.finalizeBulkDelete(sessionIds);
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to delete selected chats. Please try again.'));
      }
    });
  }

  private finalizeBulkDelete(sessionIds: string[]): void {
    this.chatDeleted$.emit(sessionIds);
    this.selectedSessionIds = [];
    this._gainsToastService.success('Selected Chats Deleted', 'The selected chats have been deleted.');
  }

  moveChatToProject(session: ChatSession, event: any): void {
    event?.stopPropagation();
    if (!session) {
      return;
    }
    this.openMoveToProjectModal([session.id], session.projectId ?? null);
  }

  bulkMoveToProject(): void {
    if (this.selectedSessionIds.length === 0) {
      return;
    }
    this.openMoveToProjectModal([...this.selectedSessionIds], null);
  }

  openMoveToProjectModal(sessionIds: string[], currentProjectId: string | null): void {
    this.modalRef = this._modalService.create({
      nzContent: DeoAiAssistantMoveToProjectComponent,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzWidth: 540,
      nzOnCancel: () => this.modalRef.close(),
      nzData: { sessionCount: sessionIds.length, currentProjectId },
      nzBodyStyle: { padding: '0' }
    });

    this.modalRef.afterClose.subscribe((result: MoveToProjectModalResult | undefined) => {
      if (!result) {
        return;
      }
      this.moveSessionsToProject(sessionIds, result.projectId);
    });
  }

  private moveSessionsToProject(sessionIds: string[], projectId: string | null): void {
    // Inside a project view, sessions reassigned to a different project leave this project's list.
    const sessionIdsLeavingProject = this.isProjectView ? sessionIds.filter(sessionId => (this.allChats.find(s => s.id === sessionId)?.projectId ?? null) !== projectId) : [];

    forkJoin(sessionIds.map(sessionId => this._aiAgentService.setSessionProject(sessionId, projectId))).subscribe({
      next: responses => {
        if (!responses.every(response => response.succeeded)) {
          this._gainsToastService.error('Error', 'Failed to move one or more chats. Please try again.');
          return;
        }
        this.applyProjectToSessions(sessionIds, projectId);
        this.clearSelection(sessionIds);
        sessionIdsLeavingProject.forEach(sessionId => this.chatRemovedFromProject$.emit(sessionId));
        this.notifyMoved(sessionIds.length);
      },
      error: error => {
        this._gainsToastService.error('Error', getApiErrorMessage(error, 'Failed to move chat. Please try again.'));
      }
    });
  }

  private applyProjectToSessions(sessionIds: string[], projectId: string | null): void {
    for (const sessionId of sessionIds) {
      const session = this.allChats.find(s => s.id === sessionId);
      if (session) {
        session.projectId = projectId;
      }
    }
  }

  private clearSelection(sessionIds: string[]): void {
    this.selectedSessionIds = this.selectedSessionIds.filter(id => !sessionIds.includes(id));
  }

  private notifyMoved(movedCount: number): void {
    const title = movedCount === 1 ? 'Chat Moved' : 'Selected Chats Moved';
    const message = movedCount === 1 ? 'The chat has been moved to the selected project.' : 'The selected chats have been moved to the selected project.';
    this._gainsToastService.success(title, message);
  }
}
